<template>
  <div :key="$store.getters['resource/currentResource'].key">

    <loader v-if="loading"/>

    <div v-else>

      <b-row>

        <b-col xl="6">
          <card-with-aside
            class="px-5 py-3"
            :title="fields.plan.title"
            :asideLeftWidth="0">
            <template slot="body">
              <formFields
                @submit="debounceSubmit"
                class="mt-2"
                :labelBlockWith="3"
                :inline="true"
                :fields="fields.plan.items"
                :prefillParams="prefillParams"
              />
            </template>
          </card-with-aside>

        </b-col>

        <b-col xl="6">

          <card-with-aside
            class="px-5 py-3"
            :title="fields.subscriptions.title"
            :asideLeftWidth="0">

            <template slot="titleButton">
              <b-button
                @click="() => {$bus.$emit('openCreateSubscriptionModal')}"
                variant="outline-secondary"
                size="sm"
                class="py-0 ml-2"
              >{{$t('button.add')}}
              </b-button>
            </template>
            <template slot="body">

              <div v-if="!prefillParams.subscriptions || !prefillParams.subscriptions.length"
                   class="text-muted text-center">
                {{$t('global.no_data')}}
              </div>

              <div v-else class="mt-2">
                <b-card
                  no-body
                  class="mb-1 border-0"
                  v-for="(subsc, index) in prefillParams.subscriptions"
                  :key="index">
                  <b-card-header
                    header-tag="header"
                    class="py-3 px-4 pointer border-0"
                    role="tab"
                    v-b-toggle="'subsc-' + index">
                    <div class="d-flex flex-row justify-content-between align-items-center">
                      <div>
                        <i class="fas fa-sort-down mr-3"></i>
                        <span>{{subsc.title}}</span>
                      </div>
                      <div>
                      <span
                        class="mr-2">{{subsc.price.toLocaleString()}} {{subsc.currency.label}}/{{getValueOf(subsc.period)}}</span>
                        <b-badge
                          :variant="subsc.active ? 'success': 'info'"
                          class="text-white ml-2 font-weight-light"
                          style="font-size: 12px">
                          {{subsc.active ? $t('form.active') : $t('form.inactive')}}
                        </b-badge>
                        <b-badge
                          :variant="subsc.public ? 'success' : 'info' "
                          class="text-white ml-2 font-weight-light"
                          style="font-size: 12px">
                          {{subsc.public ? $t('form.public') : $t('form.private') }}
                        </b-badge>
                      </div>
                    </div>
                  </b-card-header>
                  <b-collapse :id="'subsc-'+index" accordion="my-accordion" role="tabpanel">

                    <b-card-body class="position-relative d-flex flex-column pt-3 px-4 pb-0">
                      <formFields
                        :showSubmitButton="false"
                        :labelBlockWith="3"
                        :inline="true"
                        :fields="getSubscriptionFields(subsc)"
                        :prefillParams="subsc"
                      />
                      <b-btn
                        class="align-self-end p-0"
                        @click="() => {$bus.$emit('openDeleteSubscriptionModal', index)}"
                        variant="link">
                        <u class="text-secondary">{{$t('button.delete')}}</u>
                      </b-btn>
                    </b-card-body>
                  </b-collapse>
                </b-card>
              </div>

            </template>
          </card-with-aside>

        </b-col>

      </b-row>

      <!-- Access rules -->
      <b-row>
        <b-col>
          <card-with-aside
            class="px-5 py-3"
            :title="$t('plan.access_rules')"
            :asideLeftWidth="0">

            <template slot="body">
              <b-row class="align-items-center">
                <b-col sm="4" md="4" lg="3" xl="3" class="mb-4 full-access-toggle">
                  <div class="d-flex align-items-center justify-content-start mb-0">
                    <label class="switch switch-3d switch-success mr-2 mb-0">
                      <b-form-checkbox type="checkbox"
                                       class="switch-input"
                                       v-model="prefillParams.full_access"
                                       @change="onfullAccessChange"
                      />
                      <input type="checkbox"
                             class="switch-input"
                             :checked="prefillParams.full_access"
                      >
                      <span class="switch-slider"></span>
                    </label>
                    <h6 class="d-inline mb-0">{{$t('plan.full_access')}}</h6>
                  </div>
                </b-col>
                <b-col sm="8" md="8" lg="8" xl="3" class="mb-4 predictive-paywall-toggle" v-show="!prefillParams.full_access">
                  <div class="d-flex align-items-center justify-content-start mb-0">
                    <label class="switch switch-3d switch-success mr-2 mb-0">
                      <b-form-checkbox type="checkbox"
                                       class="switch-input"
                                       v-model="prefillParams.predictive_paywall"
                                       :disabled="prefillParams.full_access"
                      />
                      <input type="checkbox"
                             class="switch-input"
                             :checked="prefillParams.predictive_paywall"
                      >
                      <span class="switch-slider"></span>
                    </label>
                    <h6 class="d-inline mb-0">{{$t('plan.predictive_paywall')}}</h6>
                  </div>
                </b-col>
                <b-col class="range-container mb-4">
                  <PredictiveRange
                      v-show="prefillParams.predictive_paywall && !prefillParams.full_access"
                      v-model="prefillParams.predictive_percents"
                      id="range-global"
                      form-class="range-form"
                      :show-pageviews="true"
                      range-position="before"
                      :inline-layout="true"
                  />
                </b-col>
              </b-row>
              <TaxonomiesView
                  ref="taxonomiesView"
                v-show="!prefillParams.full_access && !prefillParams.predictive_paywall"
                :submit="submit"
                @submit="planFormSubmit"
                :prefillParams="prefillTaxonomies"
                :globalPredictivePaywall="prefillParams.predictive_paywall"
                :globalFullAccess="prefillParams.full_access"
              />
              <b-btn
                  @click="saveData"
                  class="align-self-end btn-width w-100 input-height mt-3"
              >
                {{ $t('button.save') }}
              </b-btn>
              <b-row v-if="isRoot">
                <b-col>
                  <b-button :to="{path: `/plans/actions/history/${this.id}`}" target="_blank" class="mt-4">
                    {{ $t('plan.show_history') }}
                  </b-button>
                </b-col>
              </b-row>
            </template>
          </card-with-aside>
        </b-col>
      </b-row>

      <!-- subscription modal-->
      <modal
        name="CreateSubscription"
        :confirm="$t('button.save')"
        :title="$t('users.create.subscription')"
        :preventHideOnOk="true"
        @ok="okButtonPressed">
        <template slot="body">
          <b-form
            v-if="fields.subscriptions"
            novalidate
            ref="subscriptionModalForm">
            <formFields
              @submit="data => {subscriptionFormSubmit(data)}"
              :emitSubmitForm="emitSubmitForm"
              :clearFields="clearFields"
              :showSubmitButton="false"
              class="px-3"
              :labelBlockWith="3"
              :inline="true"
              :fields="getSubscriptionFields({})"
              hideField="id"
              :prefillParams="{...subscriptionModel}"
            />
          </b-form>
        </template>
      </modal>

      <!--delete subscription modal-->
      <modal
        name="DeleteSubscription"
        @ok="index => {deleteSubscription(index)}">
        <template slot="body">
          <h5 class="text-center">{{$t('plan.delete_subscription')}}</h5>
        </template>
      </modal>

    </div>
  </div>
</template>

<script>
  import formFields from '@/components/global/formFields';
  import TaxonomiesView from '@/views/plans/TaxonomiesView.vue'
  import PredictiveRange from '@/views/plans/PredictiveRange.vue';
  import fields from '@/views/plans/plansFields';
  import { mapActions, mapState } from "vuex";


  export default {
    name: "PlanView",
    props: {
      id: {
        default: null
      },
      editing: {
        type: Boolean,
        default: false
      }
    },
    components: {
      formFields,
      TaxonomiesView,
      PredictiveRange
    },
    data: () => {
      return {
        clearFields: false,
        subscription: {},
        submit: false,
        emitSubmitForm: false,
        predictiveValue: 0,
        predictive_percents: 5,
        subscriptionModel: {
          active: false,
          once: false,
          public: true,
          is_allowed_upgrade_price: false,
          is_allowed_autorenew: false,
          currency: "USD",
          period:  "1 month",
          trial_period: null,
          price: 0,
          trial_price: 0,
          trial_iterations: 1,
          subscriber_type: "",
          title: "",
          slug: "",
          description: "",
          external_source_ids: "",
          vat_percent: 0,
          country_vat: [],
          is_applied_vat: false,
          type: "standard",
          limit_sponsored_slots: 1
        },
        formValidated: false,
        loading: false,
        params: {},
        paramsMutated: {},
        prefillParams: {
          full_access: true,
          subscriptions: [],
          predictive_percents: 5,
        },
        predictiveSwitch: false,
        prefillTaxonomies: [],
        fields: fields,
        subscriptionModal: false,
        isMailchimpAvailable: true,
        isCampaignMonitorAvailable: true,
        debounceSubmit: () => {
        }
      }
    },
    async created() {
      if (this.id) {
        this.setPrefillData()
      }
      this.$store.dispatch('currency/getCurrency');
      this.debounceSubmit = _.debounce(this.emitSubmit, 1000);
    },
    computed: {
      isRoot() {
        return this.$store.getters['auth/role'] === 'root';
      },
    },
    methods: {
      async setPrefillData() {
        this.loading = true;

        this.getData(`plans/${this.id}`, null, false).then(resp => {
          if (resp) {
            this.prefillParams = resp.data;
            this.prefillParams.priority = resp.data.priority.toString();
            this.prefillParams.subscriptions.map(item => item.id = item.id.toString());
            this.formatPrefillMailchimp(this.prefillParams);
            this.formatPrefillCampaignMonitorData(this.prefillParams);
            this.prefillTaxonomies = [];
            this.prefillParams.predictive_percents = resp.data.predictive_percents;
            if (this.prefillParams.taxonomies) {
              this.concatPrefillTaxonomies(this.prefillParams.taxonomies);
            }
            if (this.prefillParams.content_types) {
              this.concatPrefillTaxonomies(this.prefillParams.content_types, true);
            }
            this.params = this.prefillParams;
          } else {
            this.$router.push({path: '/plans'})
          }
        }).then(() => {
          this.loading = false;
          this.$forceUpdate()
        })
      },
      formatPrefillMailchimp(data) {
        if (data['extra']) {
          data['mailchimp_interests'] = data['extra']['mailchimp_interests'];
        }
      },
      formatPrefillCampaignMonitorData(data) {
        if (data['extra']) {
          data['campaign_subcsr_interests'] = data['extra']['campaign_subcsr_interests'];
          data['campaign_unsubcsr_interests'] = data['extra']['campaign_unsubcsr_interests'];
        }
      },
      concatPrefillTaxonomies(taxonomies, content_type = false) {
        if (!Array.isArray(taxonomies)) return;

        let sortTermsByParams = (terms) => {
            if (!Array.isArray(terms)) return;

            let termsSortedByParams = {};

            terms.forEach((term) => {
              const termsParamsKey = term.access_limit + term.access_limit_period + String(term.exclude);

              if (termsSortedByParams[termsParamsKey]) {
                termsSortedByParams[termsParamsKey].push(term);
              } else {
                termsSortedByParams[termsParamsKey] = [term];
              }
            });

            return termsSortedByParams;
        };

        if (content_type) {
          const sortedContentTypeTerms = sortTermsByParams(taxonomies);

          if (!sortedContentTypeTerms) return;

          Object.keys(sortedContentTypeTerms).forEach((key) => {
            this.prefillTaxonomies.push({
              title: this.$t('plan.content_type'),
              id: 'content_type',
              terms: sortedContentTypeTerms[key]
            });
          });
        } else {
          taxonomies.forEach((taxonomy) => {
            const sortedContentTypeTerms = sortTermsByParams(taxonomy.terms);

            if (!sortedContentTypeTerms) return;

            Object.keys(sortedContentTypeTerms).forEach((key) => {
              this.prefillTaxonomies.push({
                title: taxonomy.title,
                id: taxonomy.id,
                key: taxonomy.key,
                terms: sortedContentTypeTerms[key]
              });
            });
          });
        }
      },
      emitSubmit() {
        this.submit = !this.submit;
      },
      planFormSubmit(data) {
        // Create a deep copy to avoid mutating prefillParams directly
        const params = JSON.parse(JSON.stringify(this.prefillParams));

        // If full_access is enabled, create a new object without predictive_percents,
        // otherwise use params as is
        const requestData = (!params.predictive_paywall || params.full_access)
            ? (({ predictive_percents, ...rest }) => rest)(params)
            : params;

        // Continue processing the request:
        requestData.private = !requestData.public;
        requestData.active = requestData.active || false;
        requestData.extra = {};

        if (this.isMailchimpAvailable) {
          requestData.extra.mailchimp_interests = requestData.mailchimp_interests || [];
        }

        if (this.isCampaignMonitorAvailable) {
          requestData.extra.campaign_subcsr_interests = requestData.campaign_subcsr_interests || [];
          requestData.extra.campaign_unsubcsr_interests = requestData.campaign_unsubcsr_interests || [];
        }

        new Promise((resolve) => {
          requestData.taxonomies = data.taxonomies;
          requestData.content_types = data.content_types;
          resolve(requestData);
        }).then(data => {
          if (this.editing) {
            return this.putData(`plans/${this.id}`, data).then(({ data }) => data.id);
          } else {
            return this.postData('plans', data).then(({ data }) => data.id);
          }
        }).then(planId => {
          if (planId) {
            requestData.subscriptions.forEach(subscription => {
              let sub = {
                active: subscription.active,
                once: subscription.once,
                private: !subscription.public,
                is_allowed_upgrade_price: subscription.is_allowed_upgrade_price,
                is_allowed_autorenew: subscription.is_allowed_autorenew,
                subscriber_type: subscription.subscriber_type,
                title: subscription.title,
                slug: subscription.slug,
                description: subscription.description,
                external_source_ids: subscription.external_source_ids,
                plan_id: planId,
                price: this.formatToCents(subscription.price),
                trial_price: this.formatToCents(subscription.trial_price),
                trial_iterations: Number(this.getValueOf(subscription.trial_iterations)),
                currency: this.getValueOf(subscription.currency),
                period: this.getValueOf(subscription.period),
                trial_period: this.getValueOf(subscription.trial_period),
                next_subscription: (subscription.next_subscription && !subscription.is_allowed_autorenew) ? subscription.next_subscription.id : null,
                downgrade_subscription: subscription.downgrade_subscription ? subscription.downgrade_subscription.id : null,
                is_applied_vat: subscription.is_applied_vat,
                vat_percent: Number(subscription.vat_percent),
                country_vat: subscription.country_vat.map(item => ({
                  country_id: item.country.id,
                  vat_percent: item.vat_percent
                })),
                type: this.getValueOf(subscription.type),
                limit_sponsored_slots: Number(subscription.limit_sponsored_slots),
              };

              if (subscription.id) {
                this.putData(`subscriptions/${subscription.id}`, sub, null, true);
              } else {
                this.postData('subscriptions', sub, null, true).then(({ data }) => {
                  subscription.id = data.id;
                });
              }
            });
            return planId;
          }
        }).then(planId => {
          if (!this.editing) {
            this.$router.push({ path: `/plans/edit/${planId}` });
          }
        });
      },
      async saveData() {
        const taxonomyData = await new Promise((resolve) => {
          this.$refs.taxonomiesView.submitTaxonomy();
          this.$once('submit', (data) => {
            resolve(data);
          });
        });

        this.planFormSubmit(taxonomyData);
      },
      // saveData() {
      //   this.planFormSubmit(this.prefillParams);
      // },
      subscriptionFormSubmit(data) {
        this.subscription = data;
        this.addReactiveItemToArray(this.prefillParams.subscriptions, this.subscription);
        this.$bus.$emit('closeCreateSubscriptionModal');
        this.clearFields = true;
        this.$forceUpdate()
      },
      okButtonPressed() {
        this.emitSubmitForm = !this.emitSubmitForm;
        this.$forceUpdate()
      },
      deleteSubscription(index) {
        const subscription = this.prefillParams.subscriptions[index];
        if (subscription.id) {
          this.deleteData(`subscriptions/${subscription.id}`)
              .then((response) => {
                if(response) {
                  this.deleteItemByIndex(this.prefillParams.subscriptions, index)
                }
              })
              .catch(error => {
                console.log('delete subscription error', error)
              })
        }
      },
      getSubscriptionFields(subscription) {
        if (typeof subscription.id === 'undefined') {
          return this.fields.subscriptions.items;
        } else {
          return this.fields.subscriptions.items.map(item => {
            const field = Object.assign({}, item);
            if (field.name === 'type' && field.type === 'select') {
              field.disabled = true;
            }
            return field;
          });
        }
      },
      // updatePredictiveValue(newValue) {
      //   this.prefillParams.predictive_percents = Number(newValue);
      // },
      onfullAccessChange() {
        if (this.prefillParams.full_access) {
          this.prefillParams.predictive_paywall = false;
        }
      }
    }
  }
</script>

<style scoped>
  .full-access-toggle {
    .switch-3d {
      min-width: 40px;
    }
  }
  .predictive-paywall-toggle {
    .switch-3d {
      min-width: 40px;
    }
  }
  .range-container {
    @media only screen and (min-width: 1200px) {
      border-left: 1px solid #cccccc;
    }
  }
</style>
